/**
 * Admin · Children — searchable directory of every child in the workspace.
 * Links to the existing child detail flow via Workspace.jsx.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty, AdminEmptyCard } from "./_shared";

export default function AdminChildren() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [children, setChildren] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!workspace) return;
    api.get("/children").then((r) => {
      setChildren(
        (r.data || []).filter((c) => c.workspace_id === workspace.id)
      );
    });
  }, [workspace?.id]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return children;
    return children.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.group_name || "").toLowerCase().includes(q)
    );
  }, [children, search]);

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmptyCard
        testid="admin-children-no-ws"
        eyebrow="Children"
        title="A workspace comes first."
        body="Create your center and you'll be able to add children, assign them to groups, and watch their calm rhythm in one place."
        action={
          <Link
            to="/workspace"
            data-testid="admin-children-create-workspace"
            className="btn-primary"
          >
            Create a workspace
          </Link>
        }
      />
    );
  if (children.length === 0)
    return (
      <div>
        <AdminHeader eyebrow="Children" title="0 in your care" />
        <AdminEmptyCard
          testid="admin-children-first"
          eyebrow="Welcome"
          title="Add your first child."
          body="Once a child is in your workspace, parents can be linked, calm status can flow, and the day starts to write itself."
          action={
            <Link to="/workspace" data-testid="admin-children-add-first" className="btn-primary">
              Add a child
            </Link>
          }
        />
      </div>
    );

  return (
    <div data-testid="admin-children-screen">
      <AdminHeader
        eyebrow="Children"
        title={`${children.length} in your care`}
        action={
          <Link
            to="/workspace"
            data-testid="admin-children-add"
            className="btn-primary !py-2 !px-4 !text-xs"
          >
            Add a child
          </Link>
        }
      />

      <div className="mt-6 flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2 max-w-md">
        <Search size={14} strokeWidth={1.5} className="text-[#525252]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search a name or group…"
          data-testid="admin-children-search"
          className="bg-transparent outline-none text-sm flex-1"
        />
      </div>

      {visible.length === 0 ? (
        <AdminEmpty testid="admin-children-empty">
          No children match.
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-children-list"
          className="mt-6 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {visible.map((c) => (
            <li
              key={c.id}
              data-testid={`admin-children-row-${c.id}`}
              className="flex items-center gap-4 px-5 py-4"
            >
              <div className="h-11 w-11 rounded-full overflow-hidden bg-[#F2EFEA] shrink-0">
                {c.portrait_url ? (
                  <img
                    src={c.portrait_url}
                    alt={c.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center font-serif italic text-[#8A9984]">
                    {c.name[0]}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-[#171717] truncate">
                  {c.name}
                </div>
                <div className="text-xs text-[#525252] mt-0.5">
                  {c.group_name || "Unassigned"}
                  {c.age && <span> · {c.age}</span>}
                </div>
              </div>
              <span className="trust-pill" data-testid={`admin-children-row-${c.id}-state`}>
                {c.current_state}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
